import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import api from '../api';
import { SubathonState } from '../types/subathon';

function getSocketBase() {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
  return base.replace(/\/api\/?$/, '') || window.location.origin;
}

/**
 * Estado del extensible en el admin. El backend manda: las acciones devuelven el
 * estado nuevo y el socket lo replica en el resto de las pantallas abiertas.
 */
export function useSubathonAdmin() {
  const [state, setState] = useState<SubathonState | null>(null);
  const [overlayKey, setOverlayKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const offsetRef = useRef(0);

  const applyState = useCallback((next: SubathonState) => {
    setState(next);
    offsetRef.current = Date.parse(next.serverNow) - Date.now();
    if (next.overlayKey) setOverlayKey(next.overlayKey);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get<SubathonState>('/subathon/state');
      applyState(data);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo cargar el extensible');
    } finally {
      setLoading(false);
    }
  }, [applyState]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!overlayKey) return undefined;

    const socket: Socket = io(getSocketBase(), { path: '/api/socket.io' });
    socket.on('connect', () => socket.emit('join-subathon', { key: overlayKey }));
    socket.on('subathon:state', (next: SubathonState) => applyState({ ...next, overlayKey }));

    return () => {
      socket.emit('leave-subathon', { key: overlayKey });
      socket.disconnect();
    };
  }, [overlayKey, applyState]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const remainingMs = useMemo(() => {
    const timer = state?.timer;
    if (!timer) return 0;
    if (timer.status === 'running' && timer.endsAt) {
      return Math.max(0, Date.parse(timer.endsAt) - (now + offsetRef.current));
    }
    return timer.remainingMs;
  }, [state, now]);

  const run = useCallback(
    async (request: Promise<{ data: any }>) => {
      try {
        const { data } = await request;
        if (data && data.timer) applyState(data);
        else await refresh();
        setError('');
        return true;
      } catch (err: any) {
        setError(err?.response?.data?.message || 'No se pudo completar la acción');
        return false;
      }
    },
    [applyState, refresh],
  );

  return { state, overlayKey, remainingMs, loading, error, setError, refresh, run };
}

/** Reloj largo del extensible: siempre con horas, que pueden ser muchas. */
export function formatSubathonClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** "2 h 15 min" para totales y sumas, que se leen mejor así que con segundos. */
export function formatSpan(ms: number): string {
  const negative = ms < 0;
  const totalMinutes = Math.round(Math.abs(ms) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const text = hours > 0
    ? (minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`)
    : `${minutes} min`;
  return negative ? `-${text}` : text;
}

export function formatSeconds(seconds: number): string {
  if (Math.abs(seconds) < 60) return `${seconds} seg`;
  return formatSpan(seconds * 1000);
}
